import { useQuery } from '@apollo/client';
import { Stack } from '@mui/material';
import { Route, Routes, useParams } from 'react-router-dom';
import { Main } from '../../components';
import { ContentSafetyActions } from '../../components/ContentSafetyActions';
import {
  FindGallery,
  FindGrid,
  useFindResultsState,
} from '../../components/FindResults';
import { GET_FIND_GROUP_FINDS_QUERY, GET_FIND_GROUP_QUERY } from '../../gql';
import { FindGroupAccess } from './FindGroupAccess';
import { FindGroupSettings } from './FindGroupSettings';
import { FormattedMessage } from 'react-intl';

const GROUP_PRESET_OMITTED_KEYS = ['groups'] as const;

function FindGroupFinds({
  mode,
  canCreate,
}: {
  mode: 'grid' | 'gallery';
  canCreate: boolean;
}) {
  const { groupId } = useParams();
  const state = useFindResultsState();
  const { data, loading } = useQuery(GET_FIND_GROUP_FINDS_QUERY, {
    variables: { groupId, filters: state.filter },
    fetchPolicy: 'cache-first',
    skip: !groupId,
  });
  const finds = data?.getFindGroupFinds ?? [];
  const createHref = canCreate
    ? `/f/create?group=${encodeURIComponent(groupId ?? '')}`
    : null;

  if (mode === 'gallery') {
    return (
      <FindGallery
        finds={finds}
        loading={loading}
        state={state}
        enableGroupFilter={false}
        presetOmittedKeys={GROUP_PRESET_OMITTED_KEYS}
      />
    );
  }

  return (
    <FindGrid
      rows={finds}
      loading={loading}
      state={state}
      createHref={createHref}
      enableGroupFilter={false}
      presetOmittedKeys={GROUP_PRESET_OMITTED_KEYS}
    />
  );
}

export function FindGroupDetail({ mode }: { mode: 'grid' | 'gallery' }) {
  const { groupId } = useParams();
  const { data, loading } = useQuery(GET_FIND_GROUP_QUERY, {
    variables: { id: groupId },
    fetchPolicy: 'cache-and-network',
    skip: !groupId,
  });
  const group = data?.getFindGroup;

  return (
    <Main>
      <Stack alignItems="flex-end">
        <ContentSafetyActions
          enabled={Boolean(group?.shared)}
          entityId={groupId}
          entityType="FIND_GROUP"
          ownerUserId={group?.createdBy}
        />
      </Stack>
      {loading && !group && (
        <FormattedMessage
          id="finds.group.loading"
          defaultMessage="Loading group…"
        />
      )}
      {!loading && !group && (
        <FormattedMessage
          id="finds.group.notFound"
          defaultMessage="Group not found."
        />
      )}
      {group && (
        <Routes>
          <Route index element={<FindGroupSettings group={group} />} />
          <Route
            path="finds"
            element={
              <FindGroupFinds
                mode={mode}
                canCreate={!group.shared || (group.level ?? 0) >= 1}
              />
            }
          />
          <Route
            path="access"
            element={
              <FindGroupAccess
                groupId={group.id}
                collaborators={group.collaborators ?? []}
                readOnly={Boolean(group.shared)}
              />
            }
          />
        </Routes>
      )}
    </Main>
  );
}
